import * as React from "react";
import { Combobox, Option } from "@fluentui/react-components";
import { formatEntityTypeLabel, type EntityTypeItem } from "../TemplateServices";
import { SectionHeader, type TemplateTabViewStyles } from "./TemplateTabViewStyles";
import { MAX_ENTITY_OPTIONS_IN_DROPDOWN } from "./TemplateTabViewTypes";

type TemplateEntitySectionProps = {
  styles: TemplateTabViewStyles;
  selectedEntityType: string;
  entityTypeQuery: string;
  setEntityTypeQuery: (value: string) => void;
  entityNames: EntityTypeItem[];
  loadingEntityNames: boolean;
  handleEntityTypeChange: (value: string) => void;
};

const TemplateEntitySection: React.FC<TemplateEntitySectionProps> = ({
  styles,
  selectedEntityType,
  entityTypeQuery,
  setEntityTypeQuery,
  entityNames,
  loadingEntityNames,
  handleEntityTypeChange,
}) => {
  const normalizedEntityTypeQuery = entityTypeQuery.trim().toLowerCase();
  const matchingEntities = normalizedEntityTypeQuery.length > 0
    ? entityNames.filter((entity) => {
        const entityLabel = formatEntityTypeLabel(entity).toLowerCase();
        return entityLabel.includes(normalizedEntityTypeQuery) ||
          entity.displayName.toLowerCase().includes(normalizedEntityTypeQuery) ||
          entity.key.toLowerCase().includes(normalizedEntityTypeQuery);
      })
    : entityNames;
  const visibleEntityOptions = matchingEntities.slice(0, MAX_ENTITY_OPTIONS_IN_DROPDOWN);
  const selectedEntity = entityNames.find((entity) => entity.key === selectedEntityType);
  const entityTypeDisplayValue = selectedEntityType
    ? (selectedEntity ? formatEntityTypeLabel(selectedEntity) : selectedEntityType)
    : entityTypeQuery;

  return (
    <div className={styles.section}>
      <SectionHeader styles={styles} num={1} title="Select main entity" />
      <div style={{ fontSize: "11px", color: "#6D6E71", padding: "6px 12px 8px 12px", borderBottom: "0.25pt solid #E8F0F0" }}>
        Choose the primary data entity for this analyzer.
      </div>
      <div className={styles.sectionBody}>
        <label className={styles.fieldLabel} htmlFor="entity-type-combo">Entity type</label>
        <Combobox
          id="entity-type-combo"
          autoComplete="off"
          value={entityTypeDisplayValue}
          selectedOptions={selectedEntityType ? [selectedEntityType] : []}
          onChange={(event) => {
            setEntityTypeQuery(event.target.value || "");
          }}
          onOptionSelect={(_, data) => {
            setEntityTypeQuery("");
            handleEntityTypeChange(data.optionValue || "");
          }}
          placeholder="Select an entity type..."
          disabled={loadingEntityNames}
          listbox={{ style: { maxHeight: "200px", overflowY: "auto" } }}
          style={{ width: "160px", fontSize: "11px" }}
          positioning={{
            position: "below",
            align: "start",
            pinned: true,
            autoSize: "width",
          }}
        >
          {loadingEntityNames ? (
            <Option disabled>Loading entity types...</Option>
          ) : visibleEntityOptions.length === 0 ? (
            <Option disabled>No entity types available</Option>
          ) : (
            visibleEntityOptions.map((entity) => {
              const entityLabel = formatEntityTypeLabel(entity);
              return (
                <Option key={entity.key} value={entity.key} text={entityLabel}>
                  {entityLabel}
                </Option>
              );
            })
          )}
        </Combobox>
        {!loadingEntityNames && matchingEntities.length > MAX_ENTITY_OPTIONS_IN_DROPDOWN && (
          <div style={{ fontSize: "10px", color: "#8A8A8A", marginTop: "4px" }}>
            Showing first {MAX_ENTITY_OPTIONS_IN_DROPDOWN} matches. Type to narrow results.
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateEntitySection;
